import React from 'react';
import { useTranslation } from 'react-i18next';

export default function AuditLogTable({ logs, isLoading }) {
    const { t } = useTranslation();
    if (isLoading) return <p className="text-center text-gray-500">{t('loadingAuditLogs')}</p>;
    if (!logs || logs.length === 0) return <p className="text-center text-gray-500">{t('noAuditLogsFound')}</p>;

    const formatTimestamp = (value) => {
        if (!value) return '-';
        const parsed = new Date(value);
        if (Number.isNaN(parsed.getTime())) return String(value);
        return new Intl.DateTimeFormat('ja-JP', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hour12: false,
            timeZoneName: 'short'
        }).format(parsed);
    };

    const getRoleBadgeClass = (role) => {
        switch (role) {
            case 'SuperAdmin':
                return 'bg-purple-100 text-purple-700';
            case 'Admin':
                return 'bg-blue-100 text-blue-700';
            case 'Sales':
                return 'bg-emerald-100 text-emerald-700';
            case 'Residence':
                return 'bg-amber-100 text-amber-700';
            case 'Accounting':
                return 'bg-rose-100 text-rose-700';
            default:
                return 'bg-gray-100 text-gray-700';
        }
    };

    return (
        <div className="bg-white shadow rounded-lg border border-gray-200">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between gap-4">
                <div>
                    <h3 className="text-lg font-semibold text-gray-900">{t('viewAuditLogs')}</h3>
                    <p className="text-sm text-gray-500">最新 {logs.length} 件の操作を表示しています。</p>
                </div>
            </div>
            <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                        <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('operationTime')}</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('operator')}</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('operationType')}</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('targetObject')}</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('details')}</th>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                        {logs.map((log) => (
                            <tr key={log.id} className="align-top hover:bg-gray-50 transition-colors">
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                    <div className="font-medium text-gray-700">{formatTimestamp(log.timestamp)}</div>
                                    <div className="text-xs text-gray-400">#{log.id}</div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                    <div>{log.username || '-'}</div>
                                    <div className={`inline-flex mt-2 rounded-full px-2 py-0.5 text-xs font-medium ${getRoleBadgeClass(log.userRole)}`}>
                                        {log.userRole || 'Unknown'}
                                    </div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                                    <span className="inline-flex rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-700">
                                        {log.actionType || '-'}
                                    </span>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                                    {log.entityName || '-'}
                                </td>
                                <td className="px-6 py-4 text-sm text-gray-700">
                                    <div className="max-w-3xl whitespace-pre-wrap break-words leading-6">
                                        {log.description || '-'}
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
